import { Select, SelectItem } from '@backstage/core-components';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import Box from '@material-ui/core/Box';
import React, { useEffect, useMemo, useState } from 'react';
import { EntityKindFilter } from '@backstage/plugin-catalog-react';
import { useEntityList } from '@backstage/plugin-catalog-react';
import { CatalogApi } from '@backstage/catalog-client';
import { createApiRef } from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';

/**
 * The API reference for the {@link @backstage/catalog-client#CatalogApi}.
 * @public
 */
export const catalogApiRef = createApiRef<CatalogApi>({
    id: 'plugin.catalog.service',
  });


/**
 * Fetch and return all availible kinds.
 */
export function useAllKinds(): {
    loading: boolean;
    error?: Error;
    allKinds: string[];
  } {
    const catalogApi = useApi(catalogApiRef);

    const {
      error,
      loading,
      value: allKinds,
    } = useAsync(async () => {
      const items = await catalogApi
        .getEntityFacets({ facets: ['kind'] })
        .then(response => response.facets.kind?.map(f => f.value).sort() || []);
      return items;
    }, [catalogApi]);

    return { loading, error, allKinds: allKinds ?? [] };
  }

  /**
   * Filter and capitalize accessible kinds.
   */
  export function filterKinds(
    allKinds: string[],
    allowedKinds?: string[],
    forcedKinds?: string,
  ): Record<string, string> {
    let availableKinds = allKinds;
    if (allowedKinds) {
      availableKinds = availableKinds.filter(k =>
        allowedKinds.some(
          a => a.toLocaleLowerCase('en-US') === k.toLocaleLowerCase('en-US'),
        ),
      );
    }
    if (
      forcedKinds &&
      !allKinds.some(
        a =>
          a.toLocaleLowerCase('en-US') === forcedKinds.toLocaleLowerCase('en-US'),
      )
    ) {
      availableKinds = availableKinds.concat([forcedKinds]);
    }

    const kindsMap = availableKinds.sort().reduce((acc, kind) => {
      acc[kind.toLocaleLowerCase('en-US')] = kind;
      return acc;
    }, {} as Record<string, string>);

    return kindsMap;
  }
function useEntityKindFilter(opts: { initialFilter: string }): {
  loading: boolean;
  error?: Error;
  allKinds: string[];
  selectedKind: string;
  setSelectedKind: (kind: string) => void;
} {
  const {
    filters,
    queryParameters: { kind: kindParameter },
    updateFilters,
  } = useEntityList();

  const queryParamKind = useMemo(
    () => [kindParameter].flat()[0],
    [kindParameter],
  );

  const [selectedKind, setSelectedKind] = useState(
    queryParamKind ?? filters.kind?.value ?? opts.initialFilter,
  );

  // Set selected kinds on query parameter updates; this happens at initial page load and from
  // external updates to the page location.
  useEffect(() => {
    if (queryParamKind) {
      setSelectedKind(queryParamKind);
    }
  }, [queryParamKind]);

  // Set selected kind from filters; this happens when the kind filter is
  // updated from another component
  useEffect(() => {
    if (filters.kind?.value) {
      setSelectedKind(filters.kind?.value);
    }
  }, [filters.kind]);

  useEffect(() => {
    updateFilters({
      kind: selectedKind ? new EntityKindFilter(selectedKind) : undefined,
    });
  }, [selectedKind, updateFilters]);

  const { allKinds, loading, error } = useAllKinds();

  return {
    loading,
    error,
    allKinds: allKinds ?? [],
    selectedKind,
    setSelectedKind,
  };
}

/**
 * Props for {@link EntityKindPicker}.
 *
 * @public
 */
export interface EntityKindPickerProps {
  /**
   * Entity kinds to show in the dropdown; by default all kinds are fetched from the catalog and
   * displayed.
   */
  initialFilter?: string;
  hidden?: boolean;
}

/** @public */
export const MafEntityKindPicker = (props: EntityKindPickerProps) => {
  const { hidden, initialFilter = 'component' } = props;

  const alertApi = useApi(alertApiRef);

  const { error, selectedKind, setSelectedKind } =
    useEntityKindFilter({
      initialFilter: initialFilter,
    });

  useEffect(() => {
    if (error) {
      alertApi.post({
        message: `Failed to load entity kinds`,
        severity: 'error',
      });
    }
  }, [error, alertApi]);

  if (error) return null;

  const items: SelectItem[] = [
    {value: 'component', label: 'Domains' }
  ]
  return hidden ? null : (
    <Box pb={1} pt={1}>
      <Select
        label="Kind"
        items={items}
        disabled={true}
        selected={selectedKind.toLocaleLowerCase('en-US')}
        onChange={value => setSelectedKind(String(value))}
      />
    </Box>
  );
};